<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="grid grid-cols-11 gap-2">
    <div class="w-full mx-2 col-span-11 md:col-span-8">
      <div class="mt-2">
        <el-table
          :data="order.orderItems"
          :row-class-name="orderTableRowClassName"
          stripe
          border
          style="width: 100%"
          v-if="!order.empty()"
          height="300"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header"
        >
          <el-table-column :min-width="40" type="index" :index="indexMethod" label="#" />
          <el-table-column :min-width="90" prop="item.externalID" :label="t('itemCode')" >
            <template #default="scope">
              <div class="flex items-center justify-between">
                <span>{{scope.row.item?.externalID}}</span>
                <SpecialOrderItemDialog :orderItem="scope.row" readonly="true"/>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="220" :label="t('itemName')">
            <template #default="scope">
              <div class="truncate">
                <span>{{ scope.row.item.name }}</span>
                <span v-if="scope.row.packInfo?.packID" class="mx-1">{{ ` - ${getPackName(scope.row)}, ${scope.row.packInfo.packQuantity} ${t('pcs')}` }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="item.salePriceAmount" :label="t('price')">
            <template #default="scope">
              <div class="flex items-center gap-2">
                <span v-if="scope.row.packInfo?.packID">
                  {{ formatMoney(scope.row.packInfo.salePriceAmount, scope.row.item.salePriceCurrency) }}
                </span>
                <span v-else>
                {{ formatMoney(scope.row.item.salePriceAmount, scope.row.item.salePriceCurrency) }}
                </span>
                <CustomPriceInfoDialog :orderItem="scope.row"/>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :min-width="120"
            prop="item.soldAtPriceAmount"
            :label="t('salePrice')"
            align="center"
          >
            <template #default="scope">
              <div class="flex justify-between">
                <span>
                  {{ formatDinero(scope.row.soldAtPriceDinero) }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="120" prop="orderItemSerials" :label="t('quantity')">
            <template #default="scope">
              <div class="flex items-center">
                <div class="px-2 pt-1">
                  {{ scope.row.quantity - scope.row.returnedQuantity }}
                </div>
                <el-popover
                  v-if="scope.row.orderItemSerials.length || (scope.row.item.isSerialized && shouldShowTBD(order))"
                  placement="bottom-start"
                  :title="t('serialNumbers')"
                  :min-width="200"
                  trigger="hover"
                >
                  <template #default>
                    <p v-if="shouldShowTBD(order) && scope.row.item.isSerialized">
                      TBD
                    </p>
                    <p
                      v-else
                      v-for="(serial, i) in scope.row.orderItemSerials"
                      :key="i"
                      :class="{ 'line-through': serial.returned }"
                    >
                      {{ serial.itemSerial.number }}
                    </p>
                  </template>
                  <template #reference>
                    <div class="flex justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        height="16px"
                        width="16px"
                        version="1.1"
                        id="Capa_1"
                        viewBox="0 0 15.273 15.273"
                        xml:space="preserve"
                      >
                        <g>
                          <path
                            style="fill: #030104"
                            d="M0,1.803h0.897V13.47H0V1.803z M1.446,13.47h0.505V1.803H1.446V13.47z M2.315,13.47h0.504V1.803   H2.315V13.47z M3.482,13.47h1.104V1.803H3.482V13.47z M5.47,13.47h0.552V1.803H5.47V13.47z M6.878,13.47h2.288V1.803H6.878V13.47z    M12.84,13.47h0.752V1.803H12.84V13.47z M14.248,1.803V13.47h1.025V1.803H14.248z M10.084,13.47h0.492V1.803h-0.492V13.47z    M11.281,13.47h0.492V1.803h-0.492V13.47z"
                          />
                        </g>
                      </svg>
                    </div>
                  </template>
                </el-popover>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="extraProperties.batchNumber" :label="t('batchNumber')">
            <template #default="scope">
              <div>
                {{ (shouldShowTBD(order) && scope.row.item.isExpirable) ? 'TBD' : scope.row.extraProperties?.batchNumber }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" prop="extraProperties.expiryDate" :label="t('expiryDate')">
            <template #default="scope">
              <div>
                {{ (shouldShowTBD(order) && scope.row.item.isExpirable) ? 'TBD' : (scope.row.extraProperties?.expiryDate ? formatDate(scope.row.extraProperties?.expiryDate, 'DD-MM-YYYY') : '') }}
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="120"
            prop="returnedQuantity"
            :label="t('returnedQty')"
            align="center"
          >
          </el-table-column>

          <el-table-column :min-width="120" prop="itemTotal" :label="t('itemTotal')" align="center">
            <template #default="scope">
              {{ formatDinero(scope.row.itemTotal) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="150" :label="t('notes')">
            <template #default="scope">
              {{ scope.row.notes }}
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mt-4 font-bold">{{ t('relatedPayments') }}</div>
      <div class="mt-2">
        <el-table
          :data="transactions"
          stripe
          border
          style="width: 100%"
          v-if="!order.empty()"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header2"
        >
          <el-table-column :min-width="90" prop="externalID" :label="t('#')" />
          <el-table-column :min-width="90" prop="amountAmount" :label="t('amount')">
            <template #default="scope">
              <order-payment-edit :data="scope.row" @updated="onTxUpdated"></order-payment-edit>
            </template>
          </el-table-column>
          <el-table-column :min-width="90" prop="paymentMethod" :label="t('paymentMethod')">
            <template #default="scope">
              <div>{{ t(scope.row.paymentMethod) }}</div>
              <div v-if="scope.row.terminal && scope.row.terminal.enName" class="text-xs text-gray-500">
                ({{ scope.row.terminal.enName }})
              </div>
              <div v-if="scope.row.lastFourDigits" class="text-xs text-gray-500">
                ({{ scope.row.lastFourDigits }})
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="110" prop="createdAt" :label="t('date')">
            <template #default="scope">
              <span>
                {{ formatPostgresDate(scope.row.createdAt, 'DD/MM/YYYY hh:mm A') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="70" prop="totalPaidAmountDinero" :label="t('totalPaid')">
            <template #default="scope">
              <span :class="{ 'text-red-600': scope.row.receiptType == 'return' || scope.row.receiptType == 'refund'  }">
                {{ formatDinero(scope.row.totalPaidAmountDinero) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('currencies')">
            <template #default="scope">
              <span :class="{ 'text-red-600': scope.row.receiptType == 'return' }">
                <template v-if="scope.row?.multiCurrencyBreakdown?.length === 2 && scope.row.multiCurrencyBreakdown[1].currency === 'USD' && scope.row.multiCurrencyBreakdown[1].amount > 0">
                  {{ formatMoney(scope.row.multiCurrencyBreakdown[0].amount, scope.row.multiCurrencyBreakdown[0].currency) }}
                  + {{ formatMoney(scope.row.multiCurrencyBreakdown[1].amount, scope.row.multiCurrencyBreakdown[1].currency) }}
                  x {{scope.row.multiCurrencyBreakdown[1].exchangeRate}}
                </template>
                <template v-else-if="scope.row?.multiCurrencyBreakdown?.length > 0">
                  {{ formatMoney(scope.row.multiCurrencyBreakdown[0].amount, scope.row.multiCurrencyBreakdown[0].currency) }}
                </template>
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="90" :label="t('owed')">
            <template #default="scope">
              {{ formatDinero(getOwed(scope.row)) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="80" :label="t('cashier')">
            <template #default="scope">
              <span v-if="scope.row.userID && scope.row.user">
                {{ scope.row.user.name || scope.row.user.username }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </template>
          </el-table-column>
          <el-table-column :min-width="80" :label="t('collector')">
            <template #default="scope">
              <span v-if="scope.row.moneyCollectorID && scope.row.moneyCollector">
                {{ scope.row.moneyCollector.name || scope.row.moneyCollector.username }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('paymentNotes')">
            <template #default="scope">
              <div class="flex items-center justify-around">
                <span>
                  {{ scope.row.description }}
                </span>
                <div>
                  <button
                    class="text-black hover:text-blue-600 font-bold"
                    @click="printTx(scope.row)"
                  >
                    <span class="fa fa-print"></span>
                  </button>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div v-if="order.invoice.insuranceID" class="mt-4 font-bold">{{ t('insuranceTransactions') }}</div>
      <div v-if="order.invoice.insuranceID" class="mt-2">
        <el-table
          :data="insuranceTransactions"
          stripe
          border
          style="width: 100%"
          v-if="!order.empty()"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header2"
        >
          <el-table-column :min-width="60" prop="externalID" :label="t('paymentID')" />
          <el-table-column :min-width="90" prop="amountAmount" :label="t('amount')">
            <template #default="scope">
              <order-payment-edit :data="scope.row" @updated="onTxUpdated"></order-payment-edit>
            </template>
          </el-table-column>
          <el-table-column :min-width="90" prop="paymentMethod" :label="t('paymentMethod')">
            <template #default="scope">
              {{ t(scope.row.paymentMethod) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="110" prop="createdAt" :label="t('date')">
            <template #default="scope">
              <span>
                {{ formatPostgresDate(scope.row.createdAt, 'DD/MM/YYYY hh:mm A') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="70" prop="totalPaidAmountDinero" :label="t('totalPaid')">
            <template #default="scope">
              <span :class="{ 'text-red-600': scope.row.receiptType == 'return' }">
                {{ formatDinero(scope.row.totalPaidAmountDinero) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :min-width="90" :label="t('owed')">
            <template #default="scope">
              {{ formatDinero(getOwed(scope.row)) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="100" :label="t('paymentNotes')">
            <template #default="scope">
              <div class="flex items-center justify-around">
                <span>
                  {{ scope.row.description }}
                </span>
                <div>
                  <button
                    class="text-black hover:text-blue-600 font-bold"
                    @click="printTx(scope.row)"
                  >
                    <span class="fa fa-print"></span>
                  </button>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mt-4 flex flex-col justify-start text-lg">
        {{ t('orderJourney') }}
        <div class="mt-4 mx-5">
          <el-timeline>
            <el-timeline-item v-for="oj in order.orderJourneys" :key="oj.id"  placement="bottom">
              <div>
                <div class="font-bold flex items-center gap-2">
                  {{getOrderStatusLabel(oj.status)}}
                  <button 
                    v-if="oj.status === 'edited' && (oj as any).order_changes"
                    @click="showOrderChanges((oj as any).order_changes)"
                    class="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    {{ t('viewChanges') }}
                  </button>
                </div>
                <div class="text-sm text-gray-400 flex justify-between items-center w-1/3">
                  <div>{{formatPostgresDate(oj.createdAt?.toString() || '')}}</div>
                  <div>{{oj.user?.name}}</div>
                </div>
              </div>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>
    </div>

    <div class="w-full col-span-11 md:col-span-3 mt-2" style="font-size: 14px !important">
      <order-info :order="order" :showPrintReceipt="true" />
    </div>
  </div>

  <!-- Order Changes Dialog -->
  <el-dialog 
    v-model="orderChangesDialog.visible" 
    :title="t('orderChanges')"
    width="80%"
    max-width="1200px"
  >
        <div v-if="orderChangesDialog.changes" class="space-y-3">
      <!-- Error handling -->
      <div v-if="orderChangesDialog.changes.error" class="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {{ orderChangesDialog.changes.error }}
      </div>
      
      <!-- Individual Changes - Minimal -->
      <div v-else-if="orderChangesDialog.changes.changes && orderChangesDialog.changes.changes.length > 0">
        <ul class="space-y-2">
          <li v-for="(change, index) in orderChangesDialog.changes.changes" :key="index" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div class="flex-shrink-0 w-3 h-3 rounded-full" :class="getChangeColor(change.action)"></div>
            <div class="text-gray-800">{{ change.description }}</div>
          </li>
        </ul>
      </div>

      <!-- No Changes Message -->
      <div v-else-if="!orderChangesDialog.changes.error" class="p-4 bg-gray-100 rounded text-center text-gray-600">
        {{ t('noChangesFound') }}
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="orderChangesDialog.visible = false">{{ t('close') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import OrderInfo from './components/OrderInfo.vue'

// models
import {
  i18nOrderMessages,
  Order,
  OrderItem,
  OrderJourney
} from '@/models/sales/Order'

// tools
import { useI18n } from 'vue-i18n'

// helpers
import { formatDinero, formatMoney } from '@/utils/money'
import { parseFormat, formatPostgresDate, formatDate } from '@/utils/date'
import Dinero, { Currency, Dinero as DineroType } from 'dinero.js'
import { Transaction } from '@/models/accounting/Transaction'
import OrderPaymentEdit from './OrderPaymentEdit.vue'
import SerialInput from "@/views/inventory/items/SerialInput.vue";
import CustomPriceInfoDialog from "@/views/sales/CustomPriceInfoDialog.vue";
import SpecialOrderItemDialog from "@/views/sales/SpecialOrderItemDialog.vue";
import { useSettingsStore } from '@/stores/settingsStore';

export default defineComponent({
  name: 'order',
  computed: {
    OrderJourney() {
      return OrderJourney
    }
  },
  setup() {
    const { t } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...i18nOrderMessages.en,
          print: 'Print Receipt',
          printOrder: 'Print Order',
          makePayment: 'Make a payment',
          paymentsMade: 'Total paid amount',
          moneyOwed: 'Money Owed',
          returns: 'Order returns',
          markAsDelivered: 'Mark as delivered',
          doYouWantMarkAsDelivered: 'Do you want to mark order as delivered?',
          orderMarkedAsDelivered: 'Order marked as Delivered',
          Yes: 'Yes',
          Cancel: 'Cancel',
          relatedPayments: 'Transactions Info',
          paymentID: 'Payment ID',
          amount: 'Amount',
          date: 'Date',
          totalPaid: 'Total received or returned',
          owed: 'Owed',
          notes: 'Notes',
          edited: 'edited',
          paymentMethod: 'Payment Method',
          cashier: 'Cashier',
          collector: 'Collector',
          'debit-card': 'E-Payment',
          cash: 'Cash',
          paymentNotes: 'Payment Notes',
          currencies: "Payment currencies",
          insuranceTransactions: 'Insurance Payments',
          viewChanges: 'View Changes',
          orderChanges: 'Order Changes',
          action: 'Action',
          oldCustomer: 'Old Customer',
          newCustomer: 'New Customer',
          item: 'Item',
          quantityChange: 'Quantity Change',
          quantityChanged: 'Quantity Changed',
          changeSummary: 'Change Summary',
          totalAmountChange: 'Total Amount Change',
          stockUpdateRequired: 'Stock Update Required',
          accountingTransactionsRequired: 'Accounting Transactions Required',
          close: 'Close',
          yes: 'Yes',
          update: 'Update',
          add: 'Add',
          remove: 'Remove',
          individualChanges: 'Individual Changes',
          noChangesFound: 'No changes found',
          returnedQty: 'Returned Qty',
          credit: 'Credit',
          batchNumber: 'Batch Number',
          expiryDate: 'Expiry Date',
          salePrice: 'Sale Price'
        },
        ar: {
          ...i18nOrderMessages.ar,
          print: 'اطبع الفاتورة',
          printOrder: 'اطبع فاتورة الطلب',
          makePayment: 'تسديد مبلغ',
          paymentsMade: 'المبلغ المدفوع',
          moneyOwed: 'المبلغ المطلوب',
          returns: 'الترجيعات',
          markAsDelivered: 'تم التوصيل',
          doYouWantMarkAsDelivered: 'هل تم توصيل الطلب؟',
          orderMarkedAsDelivered: 'تم توصيل الطلب',
          Yes: 'نعم',
          Cancel: 'الغاء',
          relatedPayments: 'الحركات المالية',
          paymentID: 'رقم الحركة',
          amount: 'القيمة',
          date: 'التاريخ',
          totalPaid: 'المبلغ المستلم او المرجع',
          owed: 'الباقي',
          notes: 'الملاحظات',
          edited: 'تم تعديلة',
          paymentMethod: 'طريقة الدفع',
          cashier: 'الكاشير',
          collector: 'جامع الأموال',
          'debit-card': 'دفع الكتروني',
          cash: 'نقد',
          paymentNotes: 'ملاحظات الحركة',
          currencies: "عملات الحركة",
          insuranceTransactions: 'حركات شركة التأمين',
          viewChanges: 'عرض التغييرات',
          orderChanges: 'تغييرات الطلب',
          action: 'الإجراء',
          oldCustomer: 'العميل السابق',
          newCustomer: 'العميل الجديد',
          item: 'الصنف',
          quantityChange: 'تغيير الكمية',
          quantityChanged: 'تم تغيير الكمية',
          changeSummary: 'ملخص التغييرات',
          totalAmountChange: 'تغيير إجمالي المبلغ',
          stockUpdateRequired: 'مطلوب تحديث المخزون',
          accountingTransactionsRequired: 'مطلوب معاملات محاسبية',
          close: 'إغلاق',
          yes: 'نعم',
          update: 'تحديث',
          add: 'إضافة',
          remove: 'إزالة',
          individualChanges: 'التغييرات الفردية',
          noChangesFound: 'لم يتم العثور على تغييرات',
          returnedQty: 'الكمية المرجعة',
          credit: 'رصيد الزبون',
          batchNumber: 'رقم الوجبة',
          expiryDate: 'تاريخ الإنتهاء',
          salePrice: 'سعر البيع'
        }
      }
    })

    function getOwed(row: any) {
      let owed = 0
      owed = row.totalDueAmount > 0 ? row.totalDueAmount - row.amountAmount : 0

      const value = Dinero({
        amount: owed,
        currency: row.currency,
        precision: row.precision
      })

      return value
    }

    const orderTableRowClassName = ({ row, rowIndex }: { row: OrderItem; rowIndex: number }) => {
      if (row.quantity - row.returnedQuantity == 0) {
        return 'danger-row'
      } else if (row.returnedQuantity > 0) {
        return 'warning-row'
      }

      return ''
    }

    const isOrderFulfillmentMode = (order: Order) => {
      return useSettingsStore().getValue('order_fulfilling_module') === 'true' && 
             (order.orderType === 'delivery' || order.orderType === 'pick_up');
    }

    const shouldShowTBD = (order: Order) => {
      return isOrderFulfillmentMode(order) && 
             (order.orderStatus === 'unprocessed' || order.orderStatus === 'processing');
    }

    return { t, formatPostgresDate, getOwed, orderTableRowClassName, formatDate, isOrderFulfillmentMode, shouldShowTBD }
  },

  data() {
    return {
      order: new Order(),
      transactions: null as any,
      insuranceTransactions: [] as Transaction[],
      error: {
        title: '',
        body: ''
      },
      loading: false,
      orderChangesDialog: {
        visible: false,
        changes: null as any
      }
    }
  },

  async beforeMount() {
    await this.load()
  },

  components: {
    SpecialOrderItemDialog,
    CustomPriceInfoDialog,
    SerialInput,
    AlertBadge,
    LoadingSpinner,
    OrderInfo,
    OrderPaymentEdit
  },

  methods: {
    formatMoney,
    async load() {
      // get order
      try {
        this.loading = true
        const resp = await this.$http.get<Order>(`${Order.ENDPOINT}/${this.$route.params.id}`)
        this.order = Order.from(resp)

        this.transactions = this.order.invoice.transactions
          .filter((i) => {
            if (i.isReceipt) return i
          })
          .reverse()


        const insurancePaymentsResp = await this.$http.get<Transaction[]>(`/accounting/customerinvoices/insurance/payments?invoice_id=${this.order.invoiceID}`)
        this.insuranceTransactions = insurancePaymentsResp.map(it => Transaction.from(it))
      } catch (error: any) {
        console.log(error)
        this.error.title = error.title || 'Error'
        this.error.body = error.body || 'An error occurred'
      } finally {
        this.loading = false
      }
    },
    formatDinero,
    parseFormat,
    indexMethod(index: number) {
      return index + 1
    },
    printTx(data: any) {
      this.$router.push(this.$Route.SALES_TRANSACTIONS_RECEIPT.replace(':transactionID', data.id))
    },
    onTxUpdated() {
      this.load()
    },
    getPackName(oi: OrderItem) {
      for (const ip of oi.item.itemPacks) {
        if (ip.id == oi.packInfo.itemPackID) {
          return this.$ctx.locale == 'en' ? ip.pack?.enName: ip.pack?.arName
        }
      }
    },
    getOrderStatusLabel(status: string) {
      switch (status) {
        case 'unprocessed':
          return this.t('orderStatus.unprocessed')
        case 'processing':
          return this.t('orderStatus.processing')
        case 'ready':
          return this.t('orderStatus.ready')
        case 'on_the_way':
          return this.t('orderStatus.onTheWay')
        case 'delivered':
          return this.t('orderStatus.delivered')
        case 'partial_return':
          return this.t('orderStatus.partialReturn')
        case 'edited':
          return this.t('orderStatus.edited')
        default:
          return this.t('orderStatus.returned')
      }
    },
    showOrderChanges(changes: any) {
      try {
        // Parse the JSON string if it's a string
        this.orderChangesDialog.changes = typeof changes === 'string' ? JSON.parse(changes) : changes
        this.orderChangesDialog.visible = true
      } catch (error) {
        console.error('Error parsing order changes:', error)
        this.orderChangesDialog.changes = { error: 'Failed to parse order changes data' }
        this.orderChangesDialog.visible = true
      }
    },
    getChangeColor(action: string) {
      switch (action) {
        case 'add':
          return 'bg-green-500'
        case 'remove':
          return 'bg-red-500'
        case 'update':
          return 'bg-blue-500'
        default:
          return 'bg-gray-500'
      }
    },
    createDinero(amount: number) {
      return Dinero({
        amount: amount,
        currency: 'IQD' as Currency,
        precision: 0
      })
    }
  }
})
</script>

<style scoped>
.card {
  background: var(--secondary-bg-color);
}
</style>
